import React, { useEffect, useState } from "react";
import { CheckCheck, Trash2, Bell, Send, Info, AlertTriangle, ShieldAlert, ServerCrash,
  UserPlus, UserCheck, Globe, CreditCard, Repeat, Gift, Trophy, Cpu, Flag } from "lucide-react";
import { AdminShell } from "./AdminShell";
import { adminApi } from "./adminApi";

const ICONS = {
  "user-plus": UserPlus, "user-check": UserCheck, "globe": Globe, "credit-card": CreditCard,
  "repeat": Repeat, "gift": Gift, "trophy": Trophy, "cpu": Cpu, "alert-triangle": AlertTriangle,
  "server-crash": ServerCrash, "flag": Flag, "shield-alert": ShieldAlert, "info": Info,
};

const SEV = {
  info: { ring: "border-blue-500/40", dot: "bg-blue-500", text: "text-blue-300", chip: "bg-blue-500/15" },
  success: { ring: "border-emerald-500/40", dot: "bg-emerald-500", text: "text-emerald-300", chip: "bg-emerald-500/15" },
  warning: { ring: "border-amber-500/40", dot: "bg-amber-500", text: "text-amber-300", chip: "bg-amber-500/15" },
  critical: { ring: "border-red-500/50", dot: "bg-red-500", text: "text-red-300", chip: "bg-red-500/15" },
};

const fmtDate = (s) => { try { return new Date(s).toLocaleString("fr-FR", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }); } catch { return s; } };

export default function AdminSupervision({ embedded }) {
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState("");
  const [onlyUnread, setOnlyUnread] = useState(false);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");

  const load = () => {
    setData(null);
    const params = [];
    if (filter) params.push(`type=${filter}`);
    if (onlyUnread) params.push("unread=1");
    adminApi.events(params.join("&")).then(setData).catch(() => setData({ events: [], types: {}, counts: {}, unread: 0 }));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter, onlyUnread]);

  const types = data?.types || {};
  const counts = data?.counts || {};

  const readAll = async () => { await adminApi.markAllEventsRead(); load(); };
  const del = async (id) => { await adminApi.deleteEvent(id); load(); };
  const read = async (id) => { await adminApi.markEventRead(id); load(); };
  const makeTest = async () => { setBusy(true); await adminApi.testEvent({ type: "incident", email: false }); setBusy(false); load(); };
  const sendAllEmails = async () => {
    setBusy(true); setMsg("");
    try {
      const r = await adminApi.emailPreviewAll({});
      setMsg(r.configured ? `✅ ${r.sent}/${r.total} modèles envoyés à ${r.to}` : "⚠️ Gmail non configuré — aucun e-mail envoyé.");
    } catch (e) { setMsg("❌ " + (e.message || "Erreur")); }
    setBusy(false);
  };

  return (
    <AdminShell embedded={embedded} title="Centre de supervision" action={
      <div className="flex items-center gap-2 flex-wrap">
        <button onClick={sendAllEmails} disabled={busy} data-testid="send-all-emails-btn"
          className="flex items-center gap-2 text-xs uppercase tracking-wider bg-[#C19A6B] text-white font-semibold rounded-[5px] px-4 py-2 disabled:opacity-50">
          <Send className="w-3.5 h-3.5" /> Envoyer tous les modèles d'e-mail
        </button>
        <button onClick={makeTest} disabled={busy} data-testid="test-event-btn"
          className="flex items-center gap-2 text-xs uppercase tracking-wider border border-white/15 text-zinc-300 rounded-[5px] px-4 py-2 hover:bg-white/5">
          <Bell className="w-3.5 h-3.5" /> Événement test
        </button>
      </div>
    }>
      {msg && <div data-testid="supervision-msg" className="mb-5 text-sm bg-white/5 border border-white/10 rounded-[8px] px-4 py-3 text-zinc-200">{msg}</div>}

      <div className="flex items-center gap-2 mb-6 flex-wrap" data-testid="supervision-filters">
        <button onClick={() => setFilter("")} className={`text-xs uppercase tracking-wider rounded-[5px] px-3 py-1.5 border ${!filter ? "bg-[#C19A6B] text-white border-[#C19A6B]" : "border-white/15 text-zinc-400 hover:text-white"}`}>Tous</button>
        {Object.entries(types).map(([k, v]) => (
          <button key={k} onClick={() => setFilter(k)} data-testid={`filter-${k}`}
            className={`text-xs uppercase tracking-wider rounded-[5px] px-3 py-1.5 border ${filter === k ? "bg-[#C19A6B] text-white border-[#C19A6B]" : "border-white/15 text-zinc-400 hover:text-white"}`}>
            {v.label}{counts[k] ? ` (${counts[k]})` : ""}
          </button>
        ))}
        <label className="flex items-center gap-2 text-xs text-zinc-400 ml-2 cursor-pointer">
          <input type="checkbox" checked={onlyUnread} onChange={(e) => setOnlyUnread(e.target.checked)} data-testid="only-unread" /> Non lus
        </label>
        <div className="flex-1" />
        <button onClick={readAll} data-testid="read-all-btn" className="flex items-center gap-2 text-xs text-zinc-300 border border-white/15 rounded-[5px] px-3 py-1.5 hover:bg-white/5">
          <CheckCheck className="w-3.5 h-3.5" /> Tout marquer lu
        </button>
      </div>

      {!data && <div className="text-zinc-500 text-sm">Chargement…</div>}
      {data && data.events.length === 0 && (
        <div data-testid="supervision-empty" className="text-center py-20 text-zinc-500">
          <ShieldAlert className="w-10 h-10 mx-auto mb-3 opacity-40" />
          Aucun événement pour le moment. Les inscriptions, paiements, ventes et incidents apparaîtront ici.
        </div>
      )}

      <div className="space-y-3" data-testid="supervision-list">
        {(data?.events || []).map((e) => {
          const sev = SEV[e.severity] || SEV.info;
          const Icon = ICONS[(types[e.type] || {}).icon] || Info;
          return (
            <div key={e.id} data-testid={`event-${e.id}`}
              className={`relative flex gap-4 items-start bg-white/[0.03] border ${sev.ring} rounded-[10px] px-4 py-4 ${e.read ? "opacity-70" : ""}`}>
              {!e.read && <span className={`absolute left-0 top-0 bottom-0 w-1 rounded-l-[10px] ${sev.dot}`} />}
              <div className={`w-10 h-10 rounded-full flex items-center justify-center ${sev.chip} ${sev.text} shrink-0`}>
                <Icon className="w-5 h-5" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full ${sev.chip} ${sev.text}`}>{(types[e.type] || {}).label || e.type}</span>
                  {!e.read && <span className="text-[10px] uppercase tracking-wider text-[#C19A6B]">• Nouveau</span>}
                  <span className="text-[11px] text-zinc-500 ml-auto">{fmtDate(e.created_at)}</span>
                </div>
                <div className="text-white font-medium mt-1.5">{e.title}</div>
                {e.message && <div className="text-sm text-zinc-400 mt-1">{e.message}</div>}
                {e.meta && Object.keys(e.meta).length > 0 && (
                  <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2">
                    {Object.entries(e.meta).filter(([, v]) => v).map(([k, v]) => (
                      <span key={k} className="text-[12px] text-zinc-400"><span className="text-zinc-500">{k}:</span> {String(v)}</span>
                    ))}
                  </div>
                )}
              </div>
              <div className="flex flex-col gap-2 shrink-0">
                {!e.read && (
                  <button onClick={() => read(e.id)} data-testid={`read-${e.id}`} title="Marquer lu"
                    className="p-1.5 rounded-[5px] text-zinc-400 hover:text-emerald-300 hover:bg-white/5"><CheckCheck className="w-4 h-4" /></button>
                )}
                <button onClick={() => del(e.id)} data-testid={`del-${e.id}`} title="Supprimer"
                  className="p-1.5 rounded-[5px] text-zinc-400 hover:text-red-300 hover:bg-white/5"><Trash2 className="w-4 h-4" /></button>
              </div>
            </div>
          );
        })}
      </div>
    </AdminShell>
  );
}
